import { MedicineLanguageTabs, useMedicineWorkspace } from "./MedicineWorkspace";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  DialogContent,
  Slide,
  Box,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import { colors } from "../../../theme/designTokens";
import MedicineAiRecovery from './MedicineAiRecovery';
import { recoverableMedicineStatuses } from '../../../hooks/medicineAiRecovery.mjs';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Standardizes the fullScreen Dialog + Slide + AppBar/Toolbar-as-header
// convention previously hand-rolled in each medicine Add/Edit dialog.
export default function FullScreenFormDialog({
  open,
  onClose,
  onCloseIcon,
  title,
  children,
  headerActions,
  subtitle,
  savedAt,
  busy = false,
  page = false,
}) {
  const workspace = useMedicineWorkspace();
  const headingRef = React.useRef(null);
  React.useEffect(() => {
    if (page) {
      window.scrollTo(0, 0);
      headingRef.current?.focus({ preventScroll: true });
    }
  }, [page]);
  const setBusy = workspace?.setFormBusy;
  const resetForm = workspace?.resetForm;
  const formRef = workspace?.formRef;
  React.useEffect(() => { if (page) setBusy?.(busy); }, [page, busy, setBusy]);
  React.useEffect(() => { if (page && savedAt && formRef) formRef.current.dirty = false; }, [page, savedAt, formRef]);
  React.useEffect(() => () => { if (page) resetForm?.(); }, [page, resetForm]);
  const markDirty = event => {
    if (formRef && event.target.closest('section[id^="medicine-"]')) formRef.current.dirty = true;
  };
  if (page) return (
    <Box sx={{ minWidth: 0, width: "100%", "& img": { maxWidth: "100%" }, "& .ck-editor": { minWidth: 0, width: "100%" }, "& .ck-toolbar": { flexWrap: "wrap" } }}>
      <Box sx={{ position: "sticky", top: { xs: 56, sm: 64 }, zIndex: 10, bgcolor: colors.white, borderBottom: "1px solid", borderColor: "divider", p: { xs: 1, sm: 2 }, mb: 3, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
        <IconButton onClick={() => { if (!workspace || workspace.confirmLeave()) onClose(); }} disabled={busy} aria-label="Back to medicine list"><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: "1 1 100px", minWidth: 0 }}>
          <Typography ref={headingRef} tabIndex={-1} component="h1" variant="h5" sx={{ outline: "none", fontSize: { xs: 18, sm: 24 }, fontWeight: 700, overflowWrap: "anywhere" }}>{title}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, "& .MuiButton-root": { minHeight: 44 } }}>{headerActions}</Box>
      </Box>
      <MedicineLanguageTabs />
      {workspace?.aiRecovery && workspace.review?.data?.ready && (workspace.review.error || recoverableMedicineStatuses.has(workspace.review.data.status)) && <MedicineAiRecovery {...workspace.aiRecovery} disabled={busy} />}
      <Box component="nav" aria-label="Medicine form sections" sx={{ display: "flex", gap: 0.5, overflowX: "auto", mb: 2, pb: 1 }}>
        {[["media", "Media"], ["identification", "Identification"], ["variations", "Variations"], ["seo", "SEO"], ["sections", "Content"]].map(([id, label]) => <Button key={id} size="small" component="a" href={`#medicine-${id}`} sx={{ flexShrink: 0 }}>{label}</Button>)}
      </Box>
      <Box component="fieldset" onChangeCapture={markDirty} onInputCapture={markDirty} disabled={busy} aria-busy={busy} sx={{ border: 0, p: 0, maxWidth: 1400, mx: "auto", minWidth: 0, pointerEvents: busy ? "none" : "auto" }}>{children}</Box>
    </Box>
  );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: "relative", bgcolor: colors.white, color: colors.ink }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onCloseIcon || onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1, fontWeight: 700 }} variant="h6" component="div">
            {title}
          </Typography>
          {headerActions && <Box>{headerActions}</Box>}
        </Toolbar>
      </AppBar>
      <DialogContent>
        <Box sx={{ maxWidth: { xs: "100%", lg: 1400 }, mx: "auto" }}>
          {children}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
